import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { ArrowRight, Search, Globe, Zap, BarChart3, CheckCircle2, ChevronDown, Eye, Brain, Target, FileText } from "lucide-react";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";
import Breadcrumbs from "@/components/Breadcrumbs";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";

import SchemaScript from "@/components/SchemaScript";
const AI_MODE_HERO = {
  jpeg: "/media/google-ai-mode-citation-service-hero-1600_c0d4620a.jpg",
  jpegSrcSet: [
    "/media/google-ai-mode-citation-service-hero-640_9d24acf1.jpg 640w",
    "/media/google-ai-mode-citation-service-hero-960_d9c81610.jpg 960w",
    "/media/google-ai-mode-citation-service-hero-1280_3d92c83b.jpg 1280w",
  ].join(", "),
  webpSrcSet: [
    "/media/google-ai-mode-citation-service-hero-640_18b5e18e.webp 640w",
    "/media/google-ai-mode-citation-service-hero-960_ddf811e2.webp 960w",
    "/media/google-ai-mode-citation-service-hero-1280_8fde7046.webp 1280w",
  ].join(", "),
};
const AI_MODE_HERO_OG = `https://integrated.social${AI_MODE_HERO.jpeg}`;

const CANONICAL = "https://integrated.social/services/google-ai-mode-optimisation/";
const TITLE = "Google AI Mode Optimization Agency London: B2B";
const DESCRIPTION = "Google AI Mode optimization for B2B brands: structured content, schema and entity work that make your pages easier for AI Mode answers to cite.";
const H1 = "Google AI Mode Optimization Agency for B2B Brands";
const LOCKED_ANSWER = "Google AI Mode optimization makes your pages easier for Google's AI Mode to find, understand and cite when buyers ask complex questions. Integrated.Social works on answer-first content, consistent entity details, structured data that matches the page and the technical SEO AI Mode still relies on. Agents draft; a named senior person signs off. No one controls citations.";
const OPENING = `${LOCKED_ANSWER} It suits B2B teams losing clicks despite steady rankings; start with a Free AI Visibility Score or the AI Visibility Audit.`;
const FAQ_SUMMARY = "These answers cover what AI Mode optimization is, why strong rankings do not guarantee a place in AI Mode answers, which content and structured data help, how AI Mode differs from AI Overviews, how progress is measured and what timeline is realistic. In short, AI Mode still relies on core search systems, clear and evidenced pages make citation more likely, and no one can promise or time a citation.";

const faqs = [
  {
    q: "What is Google AI Mode optimization?",
    a: LOCKED_ANSWER
  },
  {
    q: "Our organic traffic is dropping even though our rankings have not changed. What is happening?",
    a: "Stable rankings do not explain every traffic change. AI answers may change how buyers explore a topic, but seasonality, demand, competitors and search presentation also need checking before assigning a cause. We compare Search Console and analytics trends with a fixed panel of buyer questions and cited URLs. Our free AI Prompt Improver tool (integrated.social/tools/ai-prompt-improver/) can help you write clearer test prompts; it does not control Google's source selection."
  },
  {
    q: "We rank number one for our most important keywords. Why are we not appearing in AI Mode answers?",
    a: "A number-one ranking is not a promise of selection for an AI Mode response. Google's AI features rely on core Search systems and may search related subtopics through query fan-out, so the supporting links depend on the question and response. We review indexability, buyer-question coverage, BLUF summaries and accurate structured data. These are content and SEO improvements, not a separate ranking formula or a guaranteed route into an answer."
  },
  {
    q: "How is Google AI Mode different from AI Overviews, and why does it matter for our business?",
    a: "AI Overviews appear in standard Google Search when Google's systems consider them useful. AI Mode is a dedicated experience for deeper exploration, reasoning and comparisons, with follow-up questions and text, voice, image or file inputs. Both can use query fan-out and show supporting links; their responses and sources can differ. For your business, this means reviewing how well your pages answer buyer questions across both experiences, rather than assuming identical visibility or universal buyer adoption."
  },
  {
    q: "What changes to our content will actually improve AI Mode citation rates?",
    a: "We focus on helpful, evidenced answers, clear headings, useful internal links, current business details and structured data that matches visible content. BLUF summaries and concise sections are our editorial methods for readers, not Google-required chunk sizes. We check factual claims and keep entity details consistent across your site and genuine external profiles. No change guarantees a higher citation rate; we record observations across the same buyer-question panel to assess progress."
  },
  {
    q: "How do you measure whether AI Mode Optimization is working?",
    a: "We track citation observations across a fixed panel of buyer questions, recording the prompt, date and cited URL, plus share of voice against named competitors in that panel. We review available Search Console generative AI impressions and analytics alongside those observations. Where consent and data permit, we connect identifiable referred leads to CRM stages. Referrers do not reliably isolate every Google AI Mode visit, and neither a citation nor an attributed lead proves incremental pipeline."
  },
  {
    q: "How long does it take to start appearing in Google AI Mode answers?",
    a: "There is no fixed timeline, and no one can promise a citation. Technical, schema and content changes can ship within weeks, then Google has to recrawl and reassess the pages. We log a fixed panel of buyer questions by prompt, date and cited URL each month, so any change is recorded rather than assumed."
  },
  {
    q: "Do we still need traditional SEO if we focus on AI Mode?",
    a: "Yes. Google's AI features are rooted in core Search ranking and quality systems, so crawlability, indexability, helpful content, internal links and page experience still matter. Our AI Mode Optimization work reviews those foundations alongside answer clarity, business details and accurate structured data. Google does not require a separate AI optimization layer, and meeting its requirements does not guarantee selection. See our SEO, AEO and GEO service for the wider technical foundation."
  },
  {
    q: "What structured data does Google AI Mode respond to?",
    a: "Google says structured data is not required for generative AI search and there is no special AI Mode schema to add. We use appropriate standard markup for the real page, service, business and visible FAQs, keeping it synchronized with the content. This helps describe the page within an overall SEO strategy; it does not establish passage-level citation signals, guarantee answer extraction or control source selection. Validation confirms markup consistency, not a citation outcome."
  },
  {
    q: "If we rank first, are we in the AI Mode answer?",
    a: "Not necessarily. AI Mode selects supporting links while answering the question, using Google's core Search systems and potentially related searches. A position-one result is not guaranteed a place. Our work combines helpful answers, BLUF summaries, technical SEO and accurate structured data, without claiming that any format overrides Google's selection systems. We assess actual cited URLs across a fixed question panel; a ranking alone is not evidence of inclusion."
  }
];

export default function ServiceAIMode() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/google-ai-mode-optimisation/#service",
        "name": "Google AI Mode Optimization",
        "alternateName": "AI Mode SEO",
        "description": "Google AI Mode content and technical SEO work for B2B and B2C brands: answer-first content, consistent entity details, standard structured data that matches visible pages, and citation observation. No citation or commercial outcome is guaranteed.",
        "url": CANONICAL,
        "image": AI_MODE_HERO_OG,
        "provider": { "@id": "https://integrated.social/#organization" },
        "areaServed": ["United Kingdom", "United States", "Europe", "Global"],
        "serviceType": "AI Search Optimization",
        "category": "Digital Marketing",
        "audience": [
          { "@type": "BusinessAudience", "audienceType": "B2B companies" },
          { "@type": "Audience", "audienceType": "B2C and ecommerce brands" }
        ],
        "subjectOf": [
          {
            "@type": "Article",
            "url": "https://integrated.social/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/",
            "name": "AEO 2026: The Complete Guide to Answer Engine Optimization"
          },
          {
            "@type": "Article",
            "url": "https://integrated.social/blog/ai-agents-replacing-b2b-research-phase/",
            "name": "How AI Agents Are Replacing the B2B Research Phase"
          },
          {
            "@type": "Article",
            "url": "https://integrated.social/blog/openai-cannes-2026-chatgpt-ads-b2b-commercial-intent/",
            "name": "OpenAI at Cannes 2026: ChatGPT Ads and B2B Commercial Intent"
          }
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/google-ai-mode-optimisation/#webpage",
        "url": CANONICAL,
        "name": TITLE,
        "headline": H1,
        "description": DESCRIPTION,
        "abstract": OPENING,
        "inLanguage": "en-US",
        "dateModified": "2026-11-02",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/#organization" },
        "mainEntity": { "@id": `${CANONICAL}#service` },
        "publisher": { "@id": "https://integrated.social/#organization" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
        "breadcrumb": { "@id": `${CANONICAL}#breadcrumb` }
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${CANONICAL}#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": TITLE, "item": CANONICAL }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": `${CANONICAL}#faq`,
        "isPartOf": { "@id": `${CANONICAL}#webpage` },
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      },
      {
        "@type": "Person",
        "@id": "https://integrated.social/modi-elnadi/#person",
        "name": "Modi Elnadi",
        "jobTitle": "Founder and Director of Marketing and AI Growth",
        "worksFor": { "@id": "https://integrated.social/#organization" },
        "url": "https://integrated.social/modi-elnadi/",
        "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
        "sameAs": [
          "https://www.linkedin.com/in/modielnadi/",
          "https://integrated.social/modi-elnadi/"
        ],
        "knowsAbout": [
          "Google AI Mode Optimization",
          "Answer Engine Optimization",
          "Generative Engine Optimization",
          "AI Search Visibility",
          "B2B SEO",
          "Entity Authority Building",
          "Schema Markup",
          "AI Citation Strategy"
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        locale="en-US"
        ogImage={AI_MODE_HERO_OG}
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <Header />
        <main>
          <div className="container px-6 pt-20">
            <Breadcrumbs
              includeSchema={false}
              items={[
                { label: "Services", href: "/services/" },
                { label: "Google AI Mode Optimization" },
              ]}
            />
          </div>

        {/* Hero */}
        <section className="relative pt-28 pb-20 overflow-hidden">
          <picture className="absolute inset-0 block">
            <source
              type="image/webp"
              srcSet={AI_MODE_HERO.webpSrcSet}
              sizes="100vw"
            />
            <source
              type="image/jpeg"
              srcSet={AI_MODE_HERO.jpegSrcSet}
              sizes="100vw"
            />
            <img
              src={AI_MODE_HERO.jpeg}
              alt="Illustration of AI answer cards, citation nodes and a B2B brand connection"
              width={1600}
              height={900}
              sizes="100vw"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="h-full w-full object-cover object-center opacity-20"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-transparent to-[#0a0a0a]" />
          <div className="relative max-w-6xl mx-auto px-6">
            <div className="inline-flex items-center gap-2 bg-[#4285F4]/10 border border-[#4285F4]/30 rounded-full px-4 py-1.5 mb-6">
              <Search className="w-3.5 h-3.5 text-[#4285F4]" />
              <span className="text-xs text-[#4285F4] font-medium tracking-wide uppercase">Is your brand invisible in Google AI Mode?</span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl leading-tight mb-6 max-w-4xl">
              {H1}
            </h1>
            <p data-answer-block className="text-lg text-white/70 max-w-2xl mb-6">
              {OPENING}
            </p>
            {/* RTB strip */}
            <div className="grid grid-cols-3 gap-3 mb-8 max-w-xl">
              <div className="border border-[#4285F4]/30 bg-[#4285F4]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#4285F4] font-bold text-base">Core SEO</div>
                <div className="text-white/50 text-xs mt-0.5">Crawlability and useful content</div>
              </div>
              <div className="border border-[#FF8C00]/30 bg-[#FF8C00]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#FF8C00] font-bold text-base">Human review</div>
                <div className="text-white/50 text-xs mt-0.5">A named senior person signs off</div>
              </div>
              <div className="border border-white/10 rounded-sm px-3 py-2 text-center">
                <div className="text-white font-bold text-base">Observed</div>
                <div className="text-white/50 text-xs mt-0.5">Citations recorded, not promised</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer" onClick={() => { trackCalendlyClick("service_ai_mode"); trackSectionCtaClick({ section_name: "service_ai_mode_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
                <button className="bg-[#4285F4] hover:bg-[#3367d6] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors">
                  AI Visibility <ArrowRight className="w-4 h-4" />
                </button>
              </a>
              <Link href="/services/seo-aeo-geo/">
                <button className="border border-white/20 hover:border-white/40 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                  View SEO + AEO/GEO Service
                </button>
              </Link>
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text={OPENING}
            title="Google AI Mode Optimization for B2B Brands"
            readingTime={5}
          />
        </div>

        {/* Qualified primary-source context replaces unsupported numerical claims. */}
        <section className="py-16 border-y border-white/5 bg-[#0f0f0f]">
          <div className="max-w-6xl mx-auto px-6">
            <p data-platform-source-context className="text-white/70 leading-relaxed">
              Google's <a className="text-[#4285F4] underline" href="https://developers.google.com/search/docs/fundamentals/ai-optimization-guide" target="_blank" rel="noopener noreferrer">current AI search guidance</a> says AI features rely on core Search systems, not mandatory content chunks or special AI schema. Its <a className="text-[#4285F4] underline" href="https://developers.google.com/search/docs/appearance/ai-features" target="_blank" rel="noopener noreferrer">AI features documentation</a> explains indexed, snippet-eligible supporting links; current Search Console inclusion settings also apply. Eligibility is not citation selection. <a className="text-[#4285F4] underline" href="https://support.google.com/websearch/answer/16011537?hl=en" target="_blank" rel="noopener noreferrer">Search Help</a> describes follow-up questions and multimodal inputs, with availability varying by feature and location. Our answer-first method is an editorial approach, not measured proof that a format makes citations more likely. No special markup, fixed timeline or citation outcome is promised.
            </p>
          </div>
        </section>

        {/* What Is AI Mode */}
        <section className="py-20 max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-5">What Is Google AI Mode and Why Does It Matter for B2B?</h2>
              <p className="text-white/70 mb-4">
                Google AI Mode is a dedicated AI-powered search experience for deeper exploration and complex comparisons. It combines information from the web with supporting links and lets people ask follow-up questions. For B2B teams, it is another place to examine how clearly your content answers software, agency and service evaluation questions; it is not a globally default search experience.
              </p>
              <p className="text-white/70 mb-4">
                Rankings alone do not secure a place in an AI Mode answer. Google's systems can issue related searches through query fan-out, and the links shown depend on the question and response. We review actual cited URLs rather than assume a ranking establishes inclusion or rely on an unsupported overlap percentage.
              </p>
              <p className="text-white/70">
                We build the content structure, structured data and entity signals that make B2B pages easier for AI-generated answers to cite. No one can guarantee a citation. The same work can support B2C and ecommerce brands when the agreed scope addresses their customer questions and accurate business or product details.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { icon: <Brain className="w-5 h-5 text-[#4285F4]" />, title: "Deeper Exploration", desc: "AI Mode supports reasoning, comparisons and follow-up questions. Models and features can change." },
                { icon: <Eye className="w-5 h-5 text-[#4285F4]" />, title: "Supporting Links", desc: "Responses can include links to relevant web sources. A link is an observation, not a guaranteed ranking or endorsement." },
                { icon: <Target className="w-5 h-5 text-[#4285F4]" />, title: "Multimodal Inputs", desc: "People can use text, voice, images and files. Useful visual content can complement clear text where it helps the buyer." },
                { icon: <Globe className="w-5 h-5 text-[#4285F4]" />, title: "Search Foundations", desc: "Indexability, useful content and accurate business details remain relevant. Eligibility does not guarantee inclusion." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 bg-white/3 border border-white/8 rounded-xl">
                  <div className="mt-0.5 shrink-0">{item.icon}</div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Three Games */}
        <section className="py-20 bg-[#0f0f0f] border-y border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold mb-3">The Three-Game Model: Ranking, Citation, and Agent Trust</h2>
            <p className="text-white/60 mb-12 max-w-2xl">
              We use this editorial review model to separate search foundations, observed citations and source consistency. A visibility plateau does not establish which area needs work; the available evidence guides the next review.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  num: "01",
                  color: "text-[#4285F4]",
                  border: "border-[#4285F4]/30",
                  title: "Traditional Rankings",
                  desc: "Technical SEO, indexability and helpful content remain relevant because Google's AI features rely on core Search ranking and quality systems. They do not guarantee selection.",
                  tactics: ["Technical SEO foundations", "Core Web Vitals", "Clean site architecture", "Internal link authority"]
                },
                {
                  num: "02",
                  color: "text-[#FF8C00]",
                  border: "border-[#FF8C00]/30",
                  title: "AI Citation Optimization",
                  desc: "We use clear answer-first sections for readers and review actual supporting links. Google does not require a separate scoring format or fixed content chunk size.",
                  tactics: ["BLUF answer structure", "200-400 word sections where useful to readers", "Structured data matching visible content", "Meaningful content refresh"]
                },
                {
                  num: "03",
                  color: "text-[#34A853]",
                  border: "border-[#34A853]/30",
                  title: "Agent Source Trust",
                  desc: "We check that business details and factual claims are accurate across genuine profiles and independent sources. Consistency is useful information hygiene, not a disclosed AI weighting formula.",
                  tactics: ["Reddit and community presence", "Industry directory citations", "Third-party corroboration", "Entity graph consistency"]
                }
              ].map((game, i) => (
                <div key={i} className={`p-6 bg-white/3 border ${game.border} rounded-2xl`}>
                  <div className={`text-4xl font-black ${game.color} mb-4 opacity-40`}>{game.num}</div>
                  <h3 className={`text-xl font-bold mb-3 ${game.color}`}>{game.title}</h3>
                  <p className="text-white/60 text-sm mb-5">{game.desc}</p>
                  <ul className="space-y-2">
                    {game.tactics.map((t, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white/30 shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-20 max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-3">What Our AI Mode Optimization Service Covers</h2>
          <p className="text-white/60 mb-12 max-w-2xl">A structured six-phase engagement covering technical foundations, content clarity and ongoing publishing. These phases describe our service, not guaranteed citation outcomes.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: <Search className="w-5 h-5 text-[#4285F4]" />,
                title: "AI Visibility Audit",
                desc: "We audit observed citations across an agreed panel of 50-100 buyer queries, record which competitors and URLs appear, and review technical and content gaps. The panel is an audit scope, not a population estimate or proof of why Google selected a source."
              },
              {
                icon: <FileText className="w-5 h-5 text-[#4285F4]" />,
                title: "Content Restructuring",
                desc: "We rewrite or restructure your highest-priority pages using BLUF (bottom line up front) summaries and, where useful to readers, 200-400 word sections. We map genuine buyer and follow-up questions without treating content length or query fan-out coverage as a Google requirement."
              },
              {
                icon: <Zap className="w-5 h-5 text-[#4285F4]" />,
                title: "Structured Data Implementation",
                desc: "We implement and maintain appropriate standard structured data for real page, service, business and FAQ content. We validate syntax and visible-content parity, using Google's Rich Results Test where supported. Google does not require special AI schema; validation is not evidence of citation selection."
              },
              {
                icon: <Globe className="w-5 h-5 text-[#4285F4]" />,
                title: "Entity Authority Building",
                desc: "We build accurate information consistency across your site, LinkedIn, industry publications, Reddit and comparison platforms where appropriate. Genuine corroboration helps readers assess claims; we do not manufacture mentions or claim a disclosed weighting advantage in Google's AI systems."
              },
              {
                icon: <BarChart3 className="w-5 h-5 text-[#4285F4]" />,
                title: "AI Citation Measurement",
                desc: "We set up identifiable AI-referrer segments where consent permits, monthly citation observations across monitored queries, and share-of-voice reporting against named competitors. Search Console impressions and CRM evidence add context to your monthly executive report; referrers alone cannot isolate every AI Mode visit or prove incremental pipeline."
              },
              {
                icon: <Target className="w-5 h-5 text-[#4285F4]" />,
                title: "Ongoing Content Publishing",
                desc: "We publish weekly AEO-optimized content mapped to genuine buyer questions. Agents draft; a named senior person signs off. Each post is registered in your content architecture and linked to relevant service pages. Publishing cadence does not guarantee growing citation rates or commercial results."
              }
            ].map((item, i) => (
              <div key={i} className="flex gap-5 p-6 bg-white/3 border border-white/8 rounded-2xl hover:border-[#4285F4]/30 transition-colors">
                <div className="w-10 h-10 bg-[#4285F4]/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Internal links: Related blog posts */}
        <section className="py-16 bg-[#0f0f0f] border-y border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold mb-2">From the AI Search Intelligence Blog</h2>
            <p className="text-white/50 text-sm mb-8">Evidence-led analysis of Google AI Mode, AEO, and B2B search visibility.</p>

            {/* Featured post */}
            <div className="border-l-4 border-[#4285F4] bg-[#4285F4]/5 rounded-r-2xl p-6 mb-6">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-xs bg-[#4285F4]/20 text-[#4285F4] px-3 py-1 rounded-full font-semibold">New: AI Search</span>
                <span className="text-xs text-white/40">June 28, 2026</span>
              </div>
              <h3 className="text-xl font-bold mb-2">How AI Agents Are Replacing the B2B Research Phase Before Vendors Are Contacted</h3>
              <p className="text-white/60 text-sm mb-4">Explore how AI-assisted buyer research can affect vendor evaluation and content strategy. Read the article's evidence and context rather than treating adoption estimates or forecasts as a result promised by this service.</p>
              <div className="flex flex-wrap gap-6 text-xs text-white/50 mb-4">
                <span>Buyer research</span>
                <span>Vendor evaluation</span>
                <span>Evidence and claim context</span>
              </div>
              <Link href="/blog/ai-agents-replacing-b2b-research-phase/">
                <span className="text-[#4285F4] text-sm font-semibold hover:underline flex items-center gap-1">
                  Read the full analysis <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>

            {/* Supporting posts grid */}
            <div className="grid md:grid-cols-3 gap-4">
              {[
                {
                  title: "AEO 2026: The Complete Guide to Answer Engine Optimization",
                  href: "/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/",
                  tag: "AEO Guide"
                },
                {
                  title: "OpenAI at Cannes 2026: ChatGPT Ads and B2B Commercial Intent",
                  href: "/blog/openai-cannes-2026-chatgpt-ads-b2b-commercial-intent/",
                  tag: "AI Advertising"
                },
                {
                  title: "AI Capex and Your B2B Marketing Budget",
                  href: "/blog/ai-capex-2026-b2b-marketing-budget-implications/",
                  tag: "AI Investment"
                }
              ].map((post, i) => (
                <Link key={i} href={post.href}>
                  <div className="p-4 bg-white/3 border border-white/8 rounded-xl hover:border-[#4285F4]/30 transition-colors cursor-pointer h-full">
                    <span className="text-xs text-[#4285F4] font-medium">{post.tag}</span>
                    <p className="text-sm font-medium mt-2 text-white/80 leading-snug">{post.title}</p>
                    <span className="text-xs text-white/40 mt-3 flex items-center gap-1">
                      Read post <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "SEO + AEO/GEO", desc: "Full AI search visibility stack including traditional SEO, AEO, and GEO.", href: "/services/seo-aeo-geo/" },
              { title: "Agentic AI Lead Gen", desc: "Multi-agent environments that automate B2B prospecting and outreach.", href: "/services/gemini-agentic-ai/" },
              { title: "Preferred sources", desc: "Build the entity authority and corroboration signals that support consistent citation.", href: "/services/preferred-sources-strategy/" }
            ].map((s, i) => (
              <Link key={i} href={s.href}>
                <div className="p-5 bg-white/3 border border-white/8 rounded-xl hover:border-white/20 transition-colors cursor-pointer h-full">
                  <h3 className="font-semibold mb-2 text-sm">{s.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{s.desc}</p>
                  <span className="text-[#4285F4] text-xs mt-3 flex items-center gap-1">
                    Learn more <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-[#0f0f0f] border-t border-white/5">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            <p data-faq-summary className="text-white/60 mb-10 leading-relaxed">{FAQ_SUMMARY}</p>
            <div className="space-y-3">
              <FaqAccordion
                  items={faqs}
                  headingLevel={3}
                  className="space-y-4"
                  itemClassName="border border-white/10 rounded-lg overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-5 pb-5 text-gray-400 leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 transition-transform duration-200"
                />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Start Building Your AI Mode Citation Authority</h2>
          <p className="text-white/60 max-w-xl mx-auto mb-8">
            We audit observed citations, review technical and content gaps, and build useful content with accurate structured data. The work improves page clarity and SEO readiness; it cannot control Google's citations.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/contact/">
              <button className="bg-[#4285F4] hover:bg-[#3367d6] text-white px-8 py-3.5 rounded-lg font-semibold flex items-center gap-2 transition-colors">
                Request an AI Visibility Audit <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link href="/modi-elnadi/">
              <button className="border border-white/20 hover:border-white/40 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors">
                Meet Modi Elnadi
              </button>
            </Link>
          </div>
        </section>

        {/* Author Bio */}
        <section className="py-12 border-t border-white/5 bg-[#0f0f0f]">
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex gap-5 items-start">
              <div className="w-14 h-14 rounded-full bg-[#4285F4]/20 border border-[#4285F4]/30 flex items-center justify-center shrink-0">
                <span className="text-[#4285F4] font-bold text-lg">ME</span>
              </div>
              <div>
                <p className="font-semibold mb-1">Modi Elnadi</p>
                <p className="text-white/50 text-xs mb-3">Founder and Director of Marketing and AI Growth, Integrated.Social</p>
                <p className="text-white/60 text-sm leading-relaxed">
                  Modi Elnadi is a B2B growth marketing specialist working across AI search optimization, AEO, GEO and Google AI Mode citation strategy. He founded Integrated.Social to help B2B brands build useful content and accurate schema infrastructure. His work spans SEO, agentic AI, PPC and demand generation for technology and professional services clients. No credential or citation outcome is implied by this service description.{" "}
                  <Link href="/modi-elnadi/">
                    <span className="text-[#4285F4] hover:underline">Read Modi's full profile.</span>
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Start with a Free AI Visibility Score and a scored report and gap analysis. It is an initial assessment, not an exhaustive record of every appearance in Google AI Mode, ChatGPT, Perplexity or Gemini.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_ai_mode_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalized</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_ai_mode"); trackSectionCtaClick({ section_name: "service_ai_mode_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="Google AI Mode Optimization"
          canonicalUrl="https://integrated.social/services/google-ai-mode-optimisation/"
          excerpt={DESCRIPTION}
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
